// Fyrefly PermissionProvider — org-scoped + system-level RBAC
// Fetches org-scoped permissions from app.get_my_permissions(org_id)
// AND system-level permissions from app.get_my_system_permissions().
// Caches to IndexedDB keyed by (user_id, org_id) for offline use.
// System permissions (e.g. plate_corrections.approve) are org-agnostic and
// apply globally regardless of active org context.
// Re-fetches on auth change or active org change.

import { useState, useEffect, type ReactNode } from 'react';
import { openDB } from 'idb';
import { PermissionContext } from '../hooks/usePermission';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { useOrgStore } from '../stores/orgStore';

const IDB_DB_NAME = 'fyrefly-permissions';
const IDB_STORE_NAME = 'perms';
const IDB_VERSION = 2; // bumped when system-permissions cache slot was added

async function openPermDb() {
  return openDB(IDB_DB_NAME, IDB_VERSION, {
    upgrade(db) { db.createObjectStore(IDB_STORE_NAME); },
  });
}

async function loadCached(userId: string, orgId: string): Promise<string[] | null> {
  try {
    const db = await openPermDb();
    return (await db.get(IDB_STORE_NAME, `${userId}:${orgId}`)) ?? null;
  } catch { return null; }
}

async function saveCache(userId: string, orgId: string, perms: string[]): Promise<void> {
  try {
    const db = await openPermDb();
    await db.put(IDB_STORE_NAME, perms, `${userId}:${orgId}`);
  } catch {}
}

export function PermissionProvider({ children }: { children: ReactNode }) {
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const user = useAuthStore((s) => s.user);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user || !supabase) {
        if (!cancelled) { setPermissions(new Set()); setLoading(false); }
        return;
      }

      // Serve cached org permissions immediately so UI is responsive offline.
      // System permissions are merged in below.
      const cached = activeOrgId ? await loadCached(user.id, activeOrgId) : null;
      const cachedSys = await loadCached(user.id, '__system__');
      if ((cached || cachedSys) && !cancelled) {
        setPermissions(new Set([...(cached ?? []), ...(cachedSys ?? [])]));
        setLoading(false);
      }

      if (!navigator.onLine) {
        if (!cached && !cachedSys && !cancelled) { setPermissions(new Set()); setLoading(false); }
        return;
      }

      try {
        // Fetch org-scoped permissions (if in org context) + system permissions in parallel.
        const [orgResult, sysResult] = await Promise.all([
          activeOrgId
            ? supabase.rpc('get_my_permissions', { p_org_id: activeOrgId })
            : Promise.resolve({ data: [] as string[], error: null }),
          supabase.rpc('get_my_system_permissions'),
        ]);
        if (cancelled) return;
        if (orgResult.error) throw orgResult.error;
        if (sysResult.error) throw sysResult.error;
        const orgPerms = (orgResult.data as string[]) ?? [];
        const sysPerms = (sysResult.data as string[]) ?? [];
        const merged = new Set([...orgPerms, ...sysPerms]);
        setPermissions(merged);
        setLoading(false);
        // Cache org and system permissions separately so invalidation is fine-grained.
        if (activeOrgId) await saveCache(user.id, activeOrgId, orgPerms);
        await saveCache(user.id, '__system__', sysPerms);
      } catch {
        if (!cancelled && !cached && !cachedSys) { setPermissions(new Set()); setLoading(false); }
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [user?.id, activeOrgId]);

  return (
    <PermissionContext.Provider value={{ permissions, loading }}>
      {children}
    </PermissionContext.Provider>
  );
}
